import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import type { PersonaRole } from "./personaAtom";
import { personaAtom } from "./personaAtom";

/**
 * Role-based default routes. Used as the initial default when a persona
 * has no stored preferences yet.
 */
export const ROLE_DEFAULT_ROUTES: Record<PersonaRole, string> = {
  "compliance-officer": "/",
  investigator: "/",
  "revenue-recovery": "/",
  "intelligence-analyst": "/",
  "team-leader": "/team",
  "executive-director": "/dashboard",
};

export type ThemePreference = "system" | "light" | "dark";

export interface UserPreferences {
  defaultRoute: string;
  navCollapsed: boolean;
  theme: ThemePreference;
}

/**
 * All preferences keyed by persona userId.
 * Persisted to localStorage as a single JSON blob.
 */
export type PreferencesMap = Record<string, UserPreferences>;

const STORAGE_KEY = "priorityos-preferences";
const STORAGE_VERSION = 1;

interface VersionedStore {
  version: number;
  data: PreferencesMap;
}

/**
 * Low-level storage atom. Versioned to allow future migrations.
 */
export const preferencesStoreAtom = atomWithStorage<VersionedStore>(
  STORAGE_KEY,
  { version: STORAGE_VERSION, data: {} },
);

/**
 * Build default preferences for a persona, seeding from personas.json
 * fixture data when available.
 */
export function buildDefaults(
  _userId: string,
  role: PersonaRole,
  personaDarkMode?: boolean,
): UserPreferences {
  let theme: ThemePreference = "system";
  if (personaDarkMode === true) {
    theme = "dark";
  } else if (personaDarkMode === false) {
    theme = "light";
  }

  return {
    defaultRoute: ROLE_DEFAULT_ROUTES[role] ?? "/",
    navCollapsed: true,
    theme,
  };
}

/**
 * Derived atom: preferences for the currently active persona.
 * Read returns the stored preferences or defaults.
 * Write merges a partial update into storage.
 */
export const currentPreferencesAtom = atom(
  (get): UserPreferences => {
    const persona = get(personaAtom);
    const store = get(preferencesStoreAtom);
    const stored = store.data[persona.userId];
    if (stored) return stored;
    return buildDefaults(persona.userId, persona.role);
  },
  (get, set, update: Partial<UserPreferences>) => {
    const persona = get(personaAtom);
    const store = get(preferencesStoreAtom);
    const current =
      store.data[persona.userId] ?? buildDefaults(persona.userId, persona.role);
    const updated = { ...current, ...update };
    set(preferencesStoreAtom, {
      ...store,
      data: { ...store.data, [persona.userId]: updated },
    });
  },
);
